// QuickTaskModal.jsx — create a task for a client, with a searchable team-member assigner.
import React, { useState, useEffect, useCallback } from "react";
import { api } from "../api";
import { useToast, Btn, Inp, Sel, Txa } from "../ui";
import { Ico } from "./primitives";

export default function QuickTaskModal({
  clientId,
  leadId,
  refundId,
  clientName,
  onDone,
  onClose,
}) {
  const toast = useToast();
  const [f, sf] = useState({
    taskTitle: "",
    description: "",
    category: "",
    dueDate: "",
    priority: "medium",
    status: "pending",
    assignedMembers: [],
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => sf((p) => ({ ...p, [k]: e.target.value }));

  const [pickerOpen, setPickerOpen] = useState(false);
  const [memberSearch, setMemberSearch] = useState("");
  const [allMembers, setAllMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);

  const loadMembers = useCallback(async () => {
    if (allMembers.length) return;
    setMembersLoading(true);
    try {
      const res = await api.get("/crm/teams?page=1&limit=200");
      const teams = res.data || [];
      const arrays = await Promise.all(
        teams.map((t) =>
          api.get(`/crm/teams/${t._id}/members?page=1&limit=100`)
            .then((r) => r.data || []).catch(() => [])
        )
      );
      const seen = new Set();
      const flat = arrays.flat().filter((m) => {
        if (seen.has(m._id) || m.status === "inactive") return false;
        seen.add(m._id);
        return true;
      });
      setAllMembers(flat);
    } catch { /* degrade silently */ }
    finally { setMembersLoading(false); }
  }, [allMembers.length]);

  useEffect(() => { if (pickerOpen) loadMembers(); }, [pickerOpen, loadMembers]);

  const selectedIds = new Set(f.assignedMembers.map((m) => m._id || m));

  const toggleMember = (member) => {
    sf((p) => ({
      ...p,
      assignedMembers: selectedIds.has(member._id)
        ? p.assignedMembers.filter((m) => (m._id || m) !== member._id)
        : [...p.assignedMembers, member],
    }));
  };

  const removeMember = (id) =>
    sf((p) => ({ ...p, assignedMembers: p.assignedMembers.filter((m) => (m._id || m) !== id) }));

  const filteredMembers = allMembers.filter(
    (m) =>
      !memberSearch ||
      m.fullName.toLowerCase().includes(memberSearch.toLowerCase()) ||
      m.email.toLowerCase().includes(memberSearch.toLowerCase()) ||
      (m.jobTitle || "").toLowerCase().includes(memberSearch.toLowerCase())
  );

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const link = {};
      if (clientId) link.clientId = clientId;
      if (leadId) link.leadId = leadId;
      if (refundId) link.refundId = refundId;
      await api.post("/crm/tasks", {
        ...f,
        ...link,
        assignedMembers: f.assignedMembers.map((m) => m._id || m),
      });
      toast("Task added ✓");
      onDone();
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const ROLE_PILL = {
    owner: "bg-amber-100 text-amber-800",
    admin: "bg-violet-100 text-violet-800",
    agent: "bg-blue-100 text-blue-700",
    support: "bg-sky-100 text-sky-700",
    viewer: "bg-slate-100 text-slate-600",
  };

  const initials = (name) =>
    (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ background: "rgba(10,15,30,0.6)", backdropFilter: "blur(10px)" }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[92vh] flex flex-col"
        style={{ animation: "modalIn .2s ease" }}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 flex-shrink-0">
          <div>
            <h2 className="text-sm font-black text-slate-800">Add Task</h2>
            <p className="text-xs text-slate-400">For {clientName}</p>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 transition-colors"
          >
            <Ico d="M6 18L18 6M6 6l12 12" sz={12} />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4 overflow-y-auto flex-1">
          <Inp label="Task Title *" placeholder="e.g. Send visa documents" value={f.taskTitle} onChange={set("taskTitle")} required />
          <Txa label="Description" placeholder="Details…" value={f.description} onChange={set("description")} />
          <div className="grid grid-cols-2 gap-3">
            <Inp label="Category" placeholder="visa / payment…" value={f.category} onChange={set("category")} />
            <Inp label="Due Date" type="date" value={f.dueDate} onChange={set("dueDate")} />
            <Sel
              label="Priority"
              value={f.priority}
              onChange={set("priority")}
              options={[
                { value: "low", label: "Low" },
                { value: "medium", label: "Medium" },
                { value: "high", label: "High" },
              ]}
            />
            <Sel
              label="Status"
              value={f.status}
              onChange={set("status")}
              options={[
                { value: "pending", label: "Pending" },
                { value: "in_progress", label: "In Progress" },
              ]}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Assigned Members</label>

            {f.assignedMembers.length > 0 && (
              <div className="flex flex-wrap gap-1.5 p-2 bg-slate-50 rounded-xl border border-slate-200">
                {f.assignedMembers.map((m) => {
                  const id = m._id || m;
                  const name = m.fullName || id;
                  return (
                    <span key={id} className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 text-xs font-medium px-2.5 py-1 rounded-lg">
                      <div className="w-5 h-5 rounded-full bg-gradient-to-br from-[rgb(var(--brand-rgb))] to-[rgb(var(--brand-accent-rgb))] flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0">
                        {initials(name)}
                      </div>
                      <span className="max-w-[90px] truncate">{name}</span>
                      <button type="button" onClick={() => removeMember(id)} className="text-slate-300 hover:text-rose-500 transition-colors ml-0.5">
                        <Ico d="M6 18L18 6M6 6l12 12" sz={10} />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}

            <button
              type="button"
              onClick={() => setPickerOpen((p) => !p)}
              className="w-full flex items-center gap-2 px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-500 hover:border-[rgb(var(--brand-rgb))] hover:text-slate-700 transition-colors text-left"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-slate-400">
                <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {pickerOpen
                ? "Close member picker"
                : `Assign team members${f.assignedMembers.length ? ` (${f.assignedMembers.length} selected)` : ""}`
              }
            </button>

            {pickerOpen && (
              <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                <div className="p-2 border-b border-slate-100 bg-slate-50">
                  <div className="relative">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" className="absolute left-2.5 top-1/2 -translate-y-1/2">
                      <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                      value={memberSearch}
                      onChange={(e) => setMemberSearch(e.target.value)}
                      placeholder="Search by name, email or title…"
                      className="w-full pl-8 pr-3 py-2 text-xs rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))] bg-white"
                      autoFocus
                    />
                  </div>
                </div>

                <div className="max-h-44 overflow-y-auto">
                  {membersLoading && (
                    <div className="flex items-center justify-center py-5">
                      <div className="w-5 h-5 border-2 border-[rgb(var(--brand-rgb))] border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                  {!membersLoading && !filteredMembers.length && (
                    <p className="text-center py-5 text-xs text-slate-400">
                      {allMembers.length ? "No members match your search" : "No team members found"}
                    </p>
                  )}
                  {!membersLoading && filteredMembers.map((m) => {
                    const isSel = selectedIds.has(m._id);
                    return (
                      <button
                        key={m._id}
                        type="button"
                        onClick={() => toggleMember(m)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-0 ${isSel ? "bg-[rgb(var(--brand-rgb)/0.05)]" : ""}`}
                      >
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${isSel ? "bg-[rgb(var(--brand-rgb))] border-[rgb(var(--brand-rgb))]" : "border-slate-300"}`}>
                          {isSel && <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><path d="M5 13l4 4L19 7" /></svg>}
                        </div>
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[rgb(var(--brand-rgb))] to-[rgb(var(--brand-accent-rgb))] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {initials(m.fullName)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-slate-700 truncate">{m.fullName}</p>
                          <p className="text-[10px] text-slate-400 truncate">{m.jobTitle || m.email}</p>
                        </div>
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full capitalize flex-shrink-0 ${ROLE_PILL[m.role] || "bg-slate-100 text-slate-500"}`}>
                          {m.role}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {f.assignedMembers.length > 0 && (
                  <div className="px-3 py-2 bg-slate-50 border-t border-slate-100 flex justify-between items-center">
                    <span className="text-[10px] text-slate-400">
                      {f.assignedMembers.length} member{f.assignedMembers.length !== 1 ? "s" : ""} selected
                    </span>
                    <button
                      type="button"
                      onClick={() => sf((p) => ({ ...p, assignedMembers: [] }))}
                      className="text-[10px] text-rose-500 hover:text-rose-600 font-semibold transition-colors"
                    >
                      Clear all
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-1">
            <Btn type="submit" variant="primary" className="flex-1" loading={saving}>Add Task</Btn>
            <Btn type="button" variant="secondary" onClick={onClose}>Cancel</Btn>
          </div>
        </form>
      </div>
    </div>
  );
}